import { isZero } from '@utils/validations'

import {
  MasterTransactionDto,
  PaymentProviderQrDto,
  PaymentProviderRedirectData,
  PaymentProviderRedirectKeyValue,
  PaymentProviderTransactionDto,
  PaymentProviderTransactionResponseDto,
  WalletTransactionDto,
} from './MasterTransactionDto'
import { isTransactionStatusSuccessful, isTransactionStatusUnSuccessful } from './TransactionStatus'

export interface MasterTransaction extends Omit<MasterTransactionDto, 'walletTransactions'> {
  walletTransactions: WalletTransaction[]
}

export interface WalletTransaction
  extends Omit<WalletTransactionDto, 'paymentProviderTransaction'> {
  paymentProviderTransaction: PaymentProviderTransaction
}

interface PaymentProviderTransaction extends Omit<PaymentProviderTransactionDto, 'response'> {
  response: PaymentProviderTransactionResponse
}

interface PaymentProviderTransactionResponse
  extends Omit<PaymentProviderTransactionResponseDto, 'redirect'> {
  redirect?: PaymentProviderTransactionResponseDto['redirect']
  redirectData?: {
    type: string
    method: null
    url: null
    data: PaymentProviderRedirectData
    prepared: null
    dataKeyValue?: PaymentProviderRedirectKeyValue[] | null
  }
}

export const isTransactionHistory = (data: MasterTransaction): boolean => {
  const transactions = data.walletTransactions || []

  if (isTransactionSuccess(data)) {
    return isZero(transactions.length)
  }

  return false
}

export const isTransactionUrlRedirect = (data: MasterTransaction): boolean => {
  if (!data) {
    return false
  }

  const [transaction] = data.walletTransactions || []

  if (isTransactionSuccess(data)) {
    const type = transaction?.paymentProviderTransaction?.response?.redirect?.type
    return type === 'redirect_url'
  }

  return false
}

export const getTransactionRedirectUrl = (data: MasterTransaction): string | undefined => {
  const [transaction] = data.walletTransactions || []
  return transaction?.paymentProviderTransaction?.response?.redirect?.url || undefined
}

export const isTransactionFormRedirect = (data: MasterTransaction): boolean => {
  if (!data) {
    return false
  }

  const [transaction] = data.walletTransactions || []

  if (isTransactionSuccess(data)) {
    const type = transaction?.paymentProviderTransaction?.response?.redirect?.type
    return type === 'redirect_form'
  }

  return false
}

export const getTransactionRedirectForm = (data: MasterTransaction): string | undefined => {
  const [transaction] = data.walletTransactions || []
  return transaction?.paymentProviderTransaction?.response?.redirect?.prepared || undefined
}

export const getTransactionQr = (data?: MasterTransaction): PaymentProviderQrDto | undefined => {
  const [transaction] = data?.walletTransactions || []
  return transaction?.paymentProviderTransaction?.response?.qr || undefined
}

export const hasTransactionQr = (data?: MasterTransaction): boolean =>
  Boolean(getTransactionQr(data)?.prepared)

export const isQrSvg = (qr?: PaymentProviderQrDto): boolean => qr?.method?.toLowerCase() === 'svg'

export const isQrImage = (qr?: PaymentProviderQrDto): boolean =>
  qr?.method?.toLowerCase() === 'image'

const isTransactionSuccess = (data: MasterTransaction) => {
  const [transaction] = data.walletTransactions || []
  return !!transaction?.id
}

export const isTransactionSuccessful = (data: MasterTransaction): boolean => {
  if (!data) {
    return false
  }

  const [transaction] = data.walletTransactions || []
  const redirect = transaction?.paymentProviderTransaction?.response?.redirect

  return !redirect && isTransactionStatusSuccessful(data.state.id)
}

export const isTransactionUnSuccessful = (data: MasterTransaction): boolean => {
  if (!data) {
    return false
  }

  const [transaction] = data.walletTransactions || []
  const redirect = transaction?.paymentProviderTransaction?.response?.redirect

  return !redirect && isTransactionStatusUnSuccessful(data.state.id)
}

export const normalizeMasterTransaction = (data: MasterTransactionDto): MasterTransaction => {
  const walletTransactions = (data?.walletTransactions || []).map(normalizeWallet) || []

  return {
    ...data,
    walletTransactions,
  }
}

const normalizeWallet = (x: WalletTransactionDto): WalletTransaction => {
  const redirect = x?.paymentProviderTransaction?.response?.redirect

  const redirectData =
    redirect?.type === 'key_value'
      ? redirect?.dataKeyValue ?? redirect?.data ?? null
      : redirect?.data ?? null

  const normalizedRedirectData = (() => {
    if (!redirectData) {
      return null
    }
    return Array.isArray(redirectData) ? [...redirectData] : { ...redirectData }
  })()

  return {
    ...x,
    paymentProviderTransaction: {
      ...x?.paymentProviderTransaction,
      response: {
        ...x?.paymentProviderTransaction?.response,
        redirect: redirect
          ? {
              ...redirect,
              data: redirectData ?? null,
            }
          : undefined,
        redirectData: redirect
          ? {
              ...redirect,
              data: normalizedRedirectData,
            }
          : undefined,
      },
    },
  }
}
